import { z } from 'zod';
import { broadcasterModuleSchema, broadcasterThemeSchema } from './broadcaster';
import {
  countryCodeSchema,
  entityIdSchema,
  isoDateTimeSchema,
  localeSchema,
  uniqueArraySchema,
} from './common';
import { ageRangeRule, contentItemFieldsSchema, contentItemSchema } from './content';
import { checkMediaAsset, mediaAssetFieldsSchema } from './media';
import { missionFieldsSchema } from './mission';
import { publicCatalogueItemSchema } from './public-catalogue';
import { grantWindowRule, rightsGrantFieldsSchema, rightsGrantSchema } from './rights';

/**
 * Editorial (staff CMS) API inputs and outputs. Inputs reuse the stored contracts' field shapes so
 * one definition governs the database rows, the API and the browser. Publication state, publish
 * time and version are server-controlled and never accepted from input.
 */

/** Every change to a versioned entity names the version it was based on (optimistic locking). */
export const expectedVersionSchema = z.int().min(1);
export const versionedActionSchema = z.strictObject({ expectedVersion: expectedVersionSchema });

const serverControlled = { publishState: true, publishedAt: true, version: true } as const;

export const contentCreateSchema = contentItemFieldsSchema
  .omit(serverControlled)
  .refine(...ageRangeRule);
export type ContentCreateInput = z.infer<typeof contentCreateSchema>;

export const contentUpdateSchema = contentItemFieldsSchema
  .omit({ ...serverControlled, id: true })
  .extend({ expectedVersion: expectedVersionSchema })
  .refine(...ageRangeRule);
export type ContentUpdateInput = z.infer<typeof contentUpdateSchema>;

export const missionCreateSchema = missionFieldsSchema
  .omit({ publishState: true, version: true })
  .refine(...ageRangeRule);
export type MissionCreateInput = z.infer<typeof missionCreateSchema>;

export const missionUpdateSchema = missionFieldsSchema
  .omit({ id: true, publishState: true, version: true })
  .extend({ expectedVersion: expectedVersionSchema })
  .refine(...ageRangeRule);
export type MissionUpdateInput = z.infer<typeof missionUpdateSchema>;

/**
 * Rights grants belong to a content item: changing one bumps the item's version. `reviewState`
 * other than `pending` may only be set by an admin (separation of duties, enforced by the API).
 */
export const grantCreateSchema = rightsGrantFieldsSchema
  .omit({ contentItemId: true })
  .extend({ expectedVersion: expectedVersionSchema })
  .refine(...grantWindowRule);
export type GrantCreateInput = z.infer<typeof grantCreateSchema>;

export const grantUpdateSchema = rightsGrantFieldsSchema
  .omit({ id: true, contentItemId: true })
  .extend({ expectedVersion: expectedVersionSchema })
  .refine(...grantWindowRule);
export type GrantUpdateInput = z.infer<typeof grantUpdateSchema>;

export const grantDeleteQuerySchema = z.strictObject({
  expectedVersion: z.coerce.number().int().min(1),
});

export const mediaCreateSchema = mediaAssetFieldsSchema.superRefine(checkMediaAsset);
export const mediaUpdateSchema = mediaAssetFieldsSchema
  .omit({ id: true })
  .superRefine(checkMediaAsset);

/** Replaces a broadcaster's curated rail; array order is the editorial order. */
export const curationUpdateSchema = z.strictObject({
  contentItemIds: uniqueArraySchema(entityIdSchema).max(200),
});

/**
 * White-label branding of a member instance (admin only, Phase 9): accent colours, the short label
 * and which optional places are offered. The catalogue and the Cinema ("watch") are always on.
 */
export const brandingUpdateSchema = z.strictObject({
  expectedVersion: z.int().min(1),
  theme: broadcasterThemeSchema,
  enabledModules: uniqueArraySchema(broadcasterModuleSchema).refine(
    (m) => m.includes('catalogue') && m.includes('watch'),
    { message: 'catalogue and watch are always enabled' },
  ),
});
export type BrandingUpdateInput = z.infer<typeof brandingUpdateSchema>;

export const idParamsSchema = z.strictObject({ id: entityIdSchema });

export const previewQuerySchema = z.strictObject({
  broadcaster: entityIdSchema,
  locale: localeSchema,
});

/** Mirrors PublishProblem in @ebu/domain (publishing.ts), which derives its type from this. */
export const publishProblemSchema = z.discriminatedUnion('rule', [
  z.strictObject({
    rule: z.literal('missing-translation'),
    field: z.enum(['title', 'description']),
    locale: localeSchema,
  }),
  z.strictObject({ rule: z.literal('no-approved-rights') }),
  z.strictObject({ rule: z.literal('rights-expired') }),
  z.strictObject({ rule: z.literal('mission-missing'), missionId: entityIdSchema }),
  z.strictObject({ rule: z.literal('mission-not-published'), missionId: entityIdSchema }),
]);
export type PublishProblem = z.infer<typeof publishProblemSchema>;

/** Rights outcome for one broadcaster territory, as staff see it (includes hidden reasons). */
export const territoryStatusSchema = z.strictObject({
  broadcasterId: entityIdSchema,
  country: countryCodeSchema,
  visibility: z.enum(['playable', 'listed-unavailable', 'hidden']),
  reason: z.string().nullable(),
  curatedPosition: z.int().min(0).nullable(),
});
export type TerritoryStatus = z.infer<typeof territoryStatusSchema>;

export const contentSummarySchema = z.strictObject({
  id: entityIdSchema,
  title: z.string(),
  format: contentItemFieldsSchema.shape.format,
  publishState: contentItemFieldsSchema.shape.publishState,
  publishedAt: isoDateTimeSchema.nullable(),
  version: z.int().min(1),
  originBroadcasterId: entityIdSchema,
  /** Locales with both title and description present. */
  localeCoverage: z.array(localeSchema),
  missionId: entityIdSchema.nullable(),
  grantCount: z.int().min(0),
  approvedGrantCount: z.int().min(0),
  territories: z.array(territoryStatusSchema),
  readiness: z.array(publishProblemSchema),
  isFictional: z.boolean(),
});
export type ContentSummary = z.infer<typeof contentSummarySchema>;

export const contentDetailSchema = z.strictObject({
  item: contentItemSchema,
  grants: z.array(rightsGrantSchema),
  territories: z.array(territoryStatusSchema),
  requiredLocales: z.array(localeSchema),
  readiness: z.array(publishProblemSchema),
});
export type ContentDetail = z.infer<typeof contentDetailSchema>;

/** What a guest of one broadcaster would see if the item were published now. */
export const contentPreviewSchema = z.strictObject({
  broadcasterId: entityIdSchema,
  locale: localeSchema,
  /** Whether the item is already in this broadcaster's curated rail (preview adds it if not). */
  curated: z.boolean(),
  visibility: territoryStatusSchema.shape.visibility,
  reason: z.string().nullable(),
  card: publicCatalogueItemSchema.nullable(),
  readiness: z.array(publishProblemSchema),
});
export type ContentPreview = z.infer<typeof contentPreviewSchema>;

/** Public broadcaster switcher entry (configuration only, no editorial data). */
export const publicBroadcasterSchema = z.strictObject({
  id: entityIdSchema,
  displayName: z.string(),
  country: countryCodeSchema,
  supportedLocales: z.array(localeSchema),
  defaultLocale: localeSchema,
  theme: broadcasterThemeSchema,
  enabledModules: z.array(broadcasterModuleSchema),
  isFictional: z.boolean(),
});
export const publicBroadcasterListSchema = z.strictObject({
  broadcasters: z.array(publicBroadcasterSchema),
});
export type PublicBroadcaster = z.infer<typeof publicBroadcasterSchema>;
